{
  "title": "Books health",
  "intro": "Checks that look for mistakes and loose ends in the books. They are rules, not AI: the same answer every time, and nothing leaves the company.",
  "period": {
    "label": "Month",
    "previous": "Previous month",
    "next": "Next month"
  },
  "counts": {
    "label": "Open problems",
    "critical": "Critical",
    "warning": "Warning",
    "info": "Note"
  },
  "severity": {
    "critical": "Critical",
    "warning": "Warning",
    "info": "Note"
  },
  "showDismissed": "Show dismissed",
  "showDismissedCount": "Show dismissed ({{n}})",
  "findingsLabel": "Problems found in {{month}}",
  "since": "Since {{date}}",
  "records": "Records",
  "ran": {
    "today": "Checks ran at {{time}}.",
    "earlier": "Checks ran on {{date}}."
  },
  "empty": {
    "title": "No problems found in {{month}}.",
    "dismissedOnly": "Nothing open. What was marked not a problem is hidden: turn on Show dismissed to see it."
  },
  "dismissedHeading": "Marked not a problem",
  "dismissed": {
    "byUntil": "{{name}} marked it not a problem until {{date}}",
    "byForGood": "{{name}} marked it not a problem for this item",
    "until": "Marked not a problem until {{date}}",
    "forGood": "Marked not a problem for this item",
    "undo": "Undo",
    "undoNamed": "Undo not a problem: {{title}}",
    "undone": "Back on the list"
  },
  "dismiss": {
    "open": "Not a problem…",
    "openNamed": "Not a problem: {{title}}",
    "title": "Not a problem",
    "body": "It stays on the list, muted, with your reason, and no longer counts as open. Anyone who may do this can undo it.",
    "reason": "Why it isn't a problem",
    "reasonPlaceholder": "For example: two deliveries on the same day, both received and checked.",
    "reasonRequired": "Say why it isn't a problem.",
    "howLong": "For how long",
    "untilDate": "Until a date",
    "untilDateBody": "After the date it comes back if it still holds.",
    "forGood": "For this item",
    "forGoodBody": "For good, on exactly these records. A new case is still found.",
    "until": "Until",
    "untilHint": "At most 180 days ahead: {{date}} at the latest.",
    "untilInvalid": "Choose a date from tomorrow up to {{date}}.",
    "save": "Mark not a problem",
    "saved": "Marked not a problem",
    "notActive": "This problem no longer holds, so there is nothing to mark. The list has been refreshed."
  },
  "skipped": {
    "title": "Not checked",
    "disabled": "Switched off: {{checks}}.",
    "no_permission": "These need access you don't have: {{checks}}.",
    "module_off": "Their module is off for your company: {{checks}}.",
    "openSettings": "Settings › Accounting checks",
    "failed": "Couldn't run this time; the other checks did: {{checks}}."
  },
  "loadError": "Books health could not be checked",
  "action": {
    "purchases": "Open purchases",
    "expenses": "Open expenses",
    "bank": "Reconcile",
    "account": "Account statement",
    "vat": "Open the VAT return",
    "balance_sheet": "Open the balance sheet",
    "journal": "Open the journal entry",
    "journals": "Open manual journals",
    "drafts": "Open drafts",
    "mapping": "Map the account",
    "aging": "Receivables aging",
    "customer": "Customer statement",
    "lock_date": "Set the lock date"
  },
  "kind": {
    "invoices": "invoices",
    "purchases": "purchases",
    "quotations": "quotations"
  },
  "side": {
    "debit": "a debit balance",
    "credit": "a credit balance"
  },
  "subledger": {
    "receivables": "the customer balances",
    "payables": "the supplier balances",
    "inventory": "the stock valuation",
    "cheques_receivable": "the cheques under collection",
    "cheques_payable": "the issued cheques not yet presented"
  },
  "units": {
    "days_one": "{{n}} day",
    "days_other": "{{n}} days",
    "bills_one": "{{n}} bill",
    "bills_other": "{{n}} bills",
    "expenses_one": "{{n}} expense",
    "expenses_other": "{{n}} expenses",
    "lines_one": "{{n}} statement line",
    "lines_other": "{{n}} statement lines",
    "invoices_one": "{{n}} invoice",
    "invoices_other": "{{n}} invoices",
    "purchases_one": "{{n}} purchase",
    "purchases_other": "{{n}} purchases",
    "quotations_one": "{{n}} quotation",
    "quotations_other": "{{n}} quotations"
  },
  "check": {
    "DUPLICATE_SUPPLIER_BILL": {
      "name": "Duplicate supplier bills",
      "title": "Possible duplicate bill from {{supplier}}",
      "detail": "{{count}}, each {{total}}, with the same supplier reference {{reference}}.",
      "detailNoReference": "{{count}}, each {{total}}, within a few days of each other.",
      "finds": "Two purchases from one supplier with the same supplier reference, or the same total within a few days."
    },
    "DUPLICATE_EXPENSE": {
      "name": "Duplicate expenses",
      "title": "Possible duplicate expense to {{payee}}",
      "detail": "{{count}}, each {{total}}, within a few days of each other.",
      "finds": "Two posted expenses with the same payee and amount within a few days."
    },
    "BANK_NOT_RECONCILED": {
      "name": "Bank accounts not reconciled",
      "title": "{{account}} not reconciled for {{days}}",
      "detail": "Last reconciled on {{lastReconciledOn}}; {{unmatched}} still to match.",
      "detailNever": "Never reconciled; {{unmatched}} still to match.",
      "finds": "A bank, card or wallet account whose last completed statement is too old, or statement lines left unmatched."
    },
    "WRONG_SIDE_BALANCE": {
      "name": "Balances on the wrong side",
      "title": "{{account}} has {{side}}",
      "detail": "{{balance}}, on the side this kind of account shouldn't carry. If this is a contra account (such as accumulated depreciation or owner's drawings), mark it Not a problem for this account.",
      "finds": "An asset or expense account with a credit balance, or a liability, equity or revenue account with a debit balance."
    },
    "NEGATIVE_CASH": {
      "name": "Cash or bank below zero",
      "title": "{{account}} is below zero",
      "detail": "Its balance at the end of the period is {{balance}}: more went out than came in.",
      "finds": "A cash or bank account with a credit balance at the end of the period."
    },
    "OPENING_BALANCE_NOT_CLEARED": {
      "name": "Opening balances not cleared",
      "title": "Opening balances left on {{account}}",
      "detail": "{{balance}} is still there after the first period: move it to retained earnings or the right accounts.",
      "finds": "A balance left on the opening-balance account after the first period."
    },
    "VAT_DIFFERENCE": {
      "name": "VAT return differences",
      "title": "The VAT return doesn't agree with the documents",
      "detail": "The ledger and the documents differ by {{difference}} for this period.",
      "finds": "The VAT return's ledger figures and its documents disagree for the period."
    },
    "BALANCE_SHEET_UNBALANCED": {
      "name": "Balance sheet out of balance",
      "title": "The balance sheet doesn't balance",
      "detail": "Assets differ from liabilities and equity by {{difference}}. This should never happen: tell your Sahab provider.",
      "finds": "The balance sheet doesn't balance. This should never happen."
    },
    "SUBLEDGER_DIFFERENCE": {
      "name": "Control accounts that don't match their records",
      "title": "{{account}} doesn't match {{subledger}}",
      "detail": "At the end of the period the account holds {{balance}}, a difference of {{difference}}. Only documents should move this account: look for an entry posted to it some other way.",
      "finds": "A receivables, payables, inventory or cheque clearing account whose balance differs from its customer, supplier, stock or cheque records."
    },
    "LARGE_MANUAL_JOURNAL": {
      "name": "Large manual journals",
      "title": "Manual journal {{number}} of {{total}}",
      "detail": "Above the review threshold of {{threshold}}: worth a second look.",
      "finds": "Manual journals above the threshold in the period, for review."
    },
    "BACKDATED_ENTRY": {
      "name": "Back-dated journals",
      "title": "Journal {{number}} was back-dated by {{days}}",
      "detail": "Dated {{entryDate}} but posted on {{postedOn}}.",
      "finds": "Manual journals dated well before the day they were posted."
    },
    "OLD_DRAFTS": {
      "name": "Old drafts",
      "title": "Old drafts: {{count}}",
      "detail": "Draft {{kind}} left longer than the limit; the oldest is from {{oldestOn}}.",
      "finds": "Draft invoices or purchases left longer than the limit."
    },
    "ROLE_NOT_MAPPED": {
      "name": "Account roles not mapped",
      "title": "No account for {{role}}",
      "detail": "Documents that post to it can't be posted until an account is chosen.",
      "finds": "Account roles your modules use that have no account."
    },
    "RECEIVABLES_OVERDUE": {
      "name": "Receivables over 90 days",
      "title": "{{pct}}% of receivables are over 90 days old",
      "detail": "{{overdue}} of the {{total}} owed by customers.",
      "finds": "Receivables over 90 days old above a share of all receivables."
    },
    "CUSTOMER_CREDIT_BALANCES": {
      "name": "Customer credit balances",
      "title": "{{customer}} has a credit of {{credit}}",
      "detail": "They paid more than they owe: refund it or apply it to an invoice.",
      "finds": "Customers who paid more than they owe, from a set amount."
    },
    "UNCATEGORISED_SPEND": {
      "name": "Uncategorised spending",
      "title": "{{pct}}% of spending is uncategorised",
      "detail": "{{amount}} went to general operating expenses in this period.",
      "finds": "Spending charged to general operating expenses above a share of the period's spending."
    },
    "PERIOD_NOT_LOCKED": {
      "name": "Past months not locked",
      "title": "Past months are still open",
      "detail": "The books are locked up to {{lockDate}}. Lock them up to {{suggested}} once those months are checked.",
      "detailNoLock": "The books have no lock date. Lock them up to {{suggested}} once those months are checked.",
      "finds": "A lock date more than one full month behind."
    }
  },
  "lockWarning": {
    "text": "Books health still finds problems in {{month}} (critical: {{critical}}, warning: {{warning}}). You can lock it anyway, but they are worth fixing first.",
    "link": "Review Books health for {{month}}",
    "impact": "Books health still finds open problems in {{month}}: critical {{critical}}, warning {{warning}}."
  },
  "settings": {
    "area": {
      "purchasing": "Purchasing",
      "expenses": "Expenses",
      "bank": "Cash and bank",
      "ledger": "Ledger",
      "vat": "VAT",
      "sales": "Sales and receivables",
      "setup": "Set-up and closing"
    },
    "on": "Runs",
    "off": "Switched off",
    "threshold": {
      "duplicateWindowDays": {
        "label": "Same amount within",
        "hint": "Two bills or two expenses within {{days}} of each other count as a possible duplicate."
      },
      "bankStaleDays": {
        "label": "Warn after",
        "hint": "A warning after {{days}} without reconciling; critical after {{twice}}."
      },
      "wrongSideMin": {
        "label": "From a balance of",
        "hint": "Balances under {{amount}} are left alone."
      },
      "openingBalanceMin": {
        "label": "From a balance of",
        "hint": "Balances under {{amount}} are left alone."
      },
      "vatDifferenceMin": {
        "label": "From a difference of",
        "hint": "Differences under {{amount}} are left alone.",
        "hintAny": "Any difference is reported."
      },
      "largeManualJournal": {
        "label": "From a total of",
        "hint": "Manual journals of {{amount}} or more are listed for review."
      },
      "backdatedDays": {
        "label": "Dated more than",
        "hint": "A manual journal dated more than {{days}} before it was posted."
      },
      "oldDraftDays": {
        "label": "Older than",
        "hint": "Drafts left for more than {{days}}."
      },
      "receivablesOverduePct": {
        "label": "Above a share of",
        "hint": "When more than {{pct}}% of receivables are over 90 days old."
      },
      "customerCreditMin": {
        "label": "From a credit of",
        "hint": "Credits under {{amount}} are left alone."
      },
      "uncategorisedPct": {
        "label": "Above a share of",
        "hint": "When more than {{pct}}% of the period's spending goes to general operating expenses."
      }
    },
    "sharedWindow": "Uses the same window as duplicate supplier bills: {{days}}.",
    "unit": {
      "days": "days",
      "pct": "%"
    },
    "range": "A whole number from 0 to {{max}}.",
    "amountInvalid": "An amount of 0 or more, with up to 3 decimals.",
    "invalid": "Some values need fixing before they can be saved.",
    "save": "Save accounting checks",
    "saved": "Accounting checks saved",
    "conflict": "Someone else changed these checks while you were editing. Reload to see their version.",
    "reload": "Reload"
  },
  "truncated": "{{check}}: showing the first {{shown}} of {{total}}. The counts include them all.",
  "draftRef": {
    "sales_invoice": "Draft invoice of {{date}}",
    "purchase": "Draft purchase of {{date}}",
    "purchaseReference": "Draft purchase {{reference}} of {{date}}",
    "other": "Draft of {{date}}"
  }
}
